import React, { useState } from 'react';
import {
  Settings2,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  List,
  Tag,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';

/**
 * Custom Values Section
 * 
 * Configure agency-specific dropdown values and controlled lists.
 */
const CustomValuesSection = ({ agency, agencyId, isReadOnly }) => {
  const [valueLists, setValueLists] = useState(agency?.custom_values || [
    {
      id: 1,
      name: 'Referral Sources',
      key: 'referral_sources',
      values: ['Google', 'Facebook', 'Referral', 'Direct', 'Other'],
      enabled: true,
    },
    {
      id: 2,
      name: 'Priority Levels',
      key: 'priority_levels',
      values: ['Low', 'Medium', 'High', 'Urgent'],
      enabled: true,
    },
    {
      id: 3,
      name: 'Case Tags',
      key: 'case_tags',
      values: ['VIP', 'Rush', 'Complex', 'Review'],
      enabled: true,
    },
  ]);
  
  const [expandedList, setExpandedList] = useState(null);
  
  return (
    <div className="space-y-6" data-testid="custom-values-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Settings2 className="text-rose-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Custom Values</h2>
              <p className="text-sm text-slate-500">
                Manage dropdown options and value lists
              </p>
            </div>
          </div>
          
          {!isReadOnly && (
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus size={16} className="mr-2" />
              Add Value List
            </Button>
          )}
        </div>
      </div>
      
      {/* Value Lists */}
      <div className="space-y-4">
        {valueLists.map((list) => (
          <div key={list.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedList(expandedList === list.id ? null : list.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <List size={18} className="text-slate-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">{list.name}</h3>
                  <p className="text-xs text-slate-500">
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded">{list.key}</code>
                    <span className="mx-2">•</span>
                    {list.values.length} values
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={list.enabled} disabled={isReadOnly} />
                {!isReadOnly && (
                  <Button variant="ghost" size="sm"><Edit2 size={14} /></Button>
                )}
              </div>
            </div>
            
            {expandedList === list.id && (
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                <div className="flex flex-wrap gap-2">
                  {list.values.map((value, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white">
                      <Tag size={12} className="mr-1" />
                      {value}
                      {!isReadOnly && (
                        <button className="ml-1 text-slate-400 hover:text-red-500">
                          <X size={12} />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {!isReadOnly && (
                    <Button variant="outline" size="sm" className="h-6">
                      <Plus size={12} className="mr-1" />
                      Add Value
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomValuesSection;
